import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const LANGUAGE_STORAGE_KEY = 'gptokens_language';
export const SUPPORTED_LANGUAGES = ['en', 'zh'];

export const dictionaries = {
  en: {
    common: {
      all: 'All',
      loading: 'Loading…',
      loadingNamed: 'Loading {name}…',
      details: 'Details',
      viewDetails: 'View details',
      backToModels: 'Back to models',
      tokens: 'tokens',
      errors: {
        prefix: 'Error: ',
        network: 'Connection interrupted. Check the operation status before trying again.',
        forbidden: 'You do not have permission to perform this operation.',
        method: 'This request method is not supported.',
        rateLimit: 'Too many requests. Please wait before continuing.',
        server: 'The server could not complete the operation. Check its status before trying again.',
        unavailable: 'Service temporarily unavailable.',
        unknown: 'An unknown error occurred.',
      },
    },
    nav: {
      models: 'Models',
      agents: 'Agents',
      price: 'Price',
      affiliate: 'Affiliate',
      docs: 'Docs',
      workspace: 'Workspace',
      login: 'Login',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      support: 'Technical Support',
    },
    home: {
      title: "GPTokens gateway for China's top LLMs",
      subtitle: '90% of the excellence, 10% of the expense',
      getApiKey: 'Get API Key',
      exploreModels: 'Explore Models',
      providerTitle: 'Deep integration with mainstream model providers',
      workflowLabel: 'Workflow',
      workflowTitle: 'Three steps to production.',
      faqLabel: 'FAQ',
      faqTitle: 'Questions before you buy.',
      faqIntro: 'Short answers for teams comparing Chinese AI model APIs, OpenAI-compatible gateways, and cheaper GPT alternatives.',
      collapse: 'Collapse answer',
      expand: 'Expand answer',
      painCards: [
        {
          visual: 'cost',
          title: 'Expensive global model bills',
          body: 'Chinese frontier models are strong for chat, code, agents, and translation while keeping token costs much lower.'
        },
        {
          visual: 'access',
          title: 'China API access is hard',
          body: 'GPTokens removes language, payment, and provider account friction with English docs and global checkout.'
        },
        {
          visual: 'route',
          title: 'Too many accounts and plans',
          body: 'Access supported models from one workspace. All your API keys share the same membership allowance and weekly limit.'
        }
      ],
      steps: [
        ['Register and create an API key', 'Sign up, enter your workspace, and create a platform API key for your app or agent.'],
        ['Choose a membership', 'Compare Plus ($9.90/month, $20 allowance), Pro ($39/month, $100) and Max ($99/month, $300). Max includes 15× the Plus allowance. Check model multipliers and weekly limits before purchase.'],
        ['Set URL and start calling models', 'Configure the GPTokens base URL and API key, then pick any model from the model list to start using it.']
      ],
      faqs: [
        ['Do weekly limits add more monthly allowance?', 'No. Weekly base limits are half the monthly allowance: $10 for Plus, $50 for Pro and $150 for Max. Weekly resets restore spending room without adding monthly allowance.'],
        ['How do Max extra packs work?', 'An eligible Max member can pay $25 for $75 extra allowance (25% of Max monthly allowance) after the base weekly or monthly allowance is exhausted. Packs carry across weeks within the same subscription period, expire at its end, and do not extend or renew the subscription.'],
        ['What payment methods do you support?', 'Available payment methods are shown at checkout and depend on your region, currency and payment provider configuration. Check the Pricing page for current membership purchase availability.'],
        ['How do I get a DeepSeek API key outside China?', 'Create a GPTokens account, review membership availability, and generate a GPTokens API key. That key can access DeepSeek models through GPTokens; GPTokens is an independent gateway and the key is issued by GPTokens, not by DeepSeek itself.'],
        ['Can I use Qwen API with an OpenAI-compatible SDK?', 'Yes. Use the GPTokens base URL with an OpenAI-compatible SDK, then choose a Qwen route such as qwen3.7-plus from the live model list.'],
        ['What is the best Chinese LLM API for coding?', 'For coding, start with DeepSeek, Qwen, Kimi, MiniMax, or GLM routes and compare accepted-answer rate, latency, tool-call reliability, and cost per completed task. GPTokens lets you test them behind one API key.'],
        ['Does GPTokens provide a Chinese LLM models list?', 'Yes. The Models page and /v1/models endpoint provide a Chinese LLM models list for supported routes including DeepSeek, Qwen, Kimi, GLM, MiniMax, MiMo, and more.'],
        ['How should I compare DeepSeek, Qwen, Kimi, GLM, and MiniMax benchmarks?', 'Use public Chinese LLM benchmark and leaderboard data only as a screening signal. Before production, run your own prompts for latency, accuracy, refusal behavior, tool use, and final cost.'],
        ['Is one API key enough for all supported China models?', 'Yes. One GPTokens API key can route requests to supported providers such as DeepSeek, MiniMax, MiMo, Qwen, GLM, and Kimi.'],
        ['How is pricing calculated?', 'Model list prices match OpenCode. The model multiplier determines allowance usage. Plus, Pro, and Max have shared monthly and weekly limits; see Pricing for terms and availability.'],
        ['Is GPTokens OpenAI-compatible?', 'Yes. The first public protocol is OpenAI-compatible, so most SDK integrations only need a new base URL, model name, and API key.']
      ],
    },
    pricing: {
      kicker: 'Monthly memberships',
      title: 'Pricing',
      subtitle: 'Choose a monthly Plus, Pro, or Max allowance. Model list prices match OpenCode; each model has a published allowance multiplier.',
      topUp: 'membership',
      paymentAmount: 'Monthly plan price',
      bonusCredits: 'Monthly allowance',
      walletCredits: 'Historical wallet balance',
      bonusRate: 'Model multiplier',
      automatic: 'Automatic',
      topUpNow: 'View plans',
      startBuilding: 'Start building',
      membershipLabel: 'Membership system',
      membershipTitle: 'The same model list price applies to every plan.',
      membershipBody: 'Your plan sets monthly and weekly allowance. Model multipliers determine how much allowance a request uses.',
      tier: 'Plan',
      cumulative: 'Monthly allowance',
      modelMultiplier: 'Model multiplier',
      benefit: 'Benefit',
      calculatorLabel: 'Allowance calculator',
      calculatorTitle: 'Estimate requests from your plan.',
      calculatorBody: 'Estimates use the model list-price cost and its published membership multiplier.',
      userState: 'User state',
      purchase: 'Plan',
      paid: 'Paid',
      bonus: 'Allowance',
      effectiveCost: 'Allowance used',
      tokenExample: 'Token example',
    },
    checkout: {
      title: 'Checkout',
      rechargePackage: 'Recharge package',
      recharge: 'Recharge',
      rechargeSubtitle: 'One-time prepaid wallet credit package',
      custom: 'custom',
      customAmount: 'Custom recharge amount',
      paymentAmount: 'Payment amount',
      bonusCredits: 'Bonus credits',
      totalCredits: 'Total wallet credits',
      bonusRate: 'Bonus rate',
      packMultiplier: 'Pack multiplier',
      bonusType: 'Bonus type',
      payment: 'Payment',
      paymentBody: 'Stripe decides the final available methods based on your region, currency, device, and our Stripe account configuration. Credits are added after Stripe confirms the payment.',
      recommendedCheckout: 'Recommended checkout',
      recommendedCheckoutNote: 'Continue with Stripe Checkout. It will show the payment methods available to you.',
      recommendedGlobally: 'Recommended globally',
      availableRegion: 'Available in your region',
      noRegionDetected: 'No regional method was detected for this session. Stripe may still show local options during checkout.',
      showMore: 'Show more payment methods',
      hideMore: 'Hide more payment methods',
      moreMethods: 'More payment methods',
      continuePayment: 'Continue to payment',
      invalidAmount: 'Please enter a valid recharge amount first.',
      updatingPreview: 'Updating recharge preview...',
      bonusTypes: {
        top_up_pack: 'top_up_pack',
        none: 'none',
      },
      paymentGroups: {
        global: 'Recommended globally',
        regional: 'Available in your region',
        more: 'More payment methods',
      },
      paymentMethods: {
        card: { name: 'Card', note: 'Usually available through Stripe Checkout.' },
        apple_google_pay: { name: 'Apple Pay / Google Pay', note: 'Shown by Stripe on supported devices and browsers.' },
        link: { name: 'Link', note: 'Fast Stripe wallet checkout when available.' },
        amazon_pay: { name: 'Amazon Pay', note: 'Usually available through Stripe Checkout.' },
        alipay: { name: 'Alipay', note: 'Popular digital wallet for China and cross-border buyers.' },
        wechat_pay: { name: 'WeChat Pay', note: 'Popular digital wallet for China and cross-border buyers.' },
        cartes_bancaires: { name: 'Cartes Bancaires', note: 'May require local card eligibility.' },
        kakao_pay: { name: 'Kakao Pay', note: 'May require local account eligibility.' },
        naver_pay: { name: 'Naver Pay', note: 'May require local account eligibility.' },
        payco: { name: 'PAYCO', note: 'May require local account eligibility.' },
        samsung_pay: { name: 'Samsung Pay', note: 'May require local device or account eligibility.' },
        mb_way: { name: 'MB WAY', note: 'May require local account eligibility.' },
        revolut_pay: { name: 'Revolut Pay', note: 'May require local account eligibility.' },
        satispay: { name: 'Satispay', note: 'May require local account eligibility.' },
        bancontact: { name: 'Bancontact', note: 'May require local bank eligibility.' },
        blik: { name: 'BLIK', note: 'May require local bank eligibility.' },
        eps: { name: 'EPS', note: 'May require local bank eligibility.' },
        pix: { name: 'Pix', note: 'May require local account eligibility.' },
      },
    },
    checkoutSuccess: {
      missingSession: 'Missing checkout session id.',
      notFound: 'Checkout session was not found. If you just paid, open Wallet and check your recharge bills.',
      paid: 'Paid',
      bonus: 'Bonus',
      total: 'Wallet credits',
      statusLabel: 'Status',
      wallet: 'Go to wallet',
      refresh: 'Refresh status',
      note: 'Stripe may take a few seconds to notify GPTokens. This page refreshes automatically while the payment is pending.',
      status: {
        pending: {
          title: 'Confirming your payment',
          body: 'Stripe has redirected you back. We are waiting for the payment confirmation webhook before adding credits.',
          label: 'Pending'
        },
        succeeded: {
          title: 'Payment successful',
          body: 'Your wallet credits have been added. You can now use them with your API keys.',
          label: 'Succeeded'
        },
        expired: {
          title: 'Checkout session expired',
          body: 'No credits were added. Start a new checkout session when you are ready to pay.',
          label: 'Expired'
        },
        failed: {
          title: 'Payment was not completed',
          body: 'No credits were added. Please try again or use another payment method.',
          label: 'Failed'
        },
      },
    },
    models: {
      search: 'Search models, providers, coding, agents...',
      supported: 'models shown',
      notFound: 'Model not found',
      notFoundTitle: 'This model is not in the GPTokens catalog yet.',
      notFoundBody: 'Return to the model directory and choose one of the currently documented models.',
      allModels: 'All models',
      apiName: 'API name',
      context: 'Context',
      modality: 'Modality',
      source: 'Source',
      overview: 'Overview',
      overviewTitle: 'What this model is good at',
      capabilities: 'Key capabilities',
      capabilitiesTitle: 'Where it fits',
      benchmarks: 'Benchmarks',
      benchmarksTitle: 'Selected public benchmark snapshot',
      benchmark: 'Benchmark',
      result: 'Result',
      benchmarkSource: 'Source',
      benchmarkNote: 'Benchmark data is a screening signal, not a production guarantee. Always test latency, tool-call reliability, refusal behavior, fallback rate, and cost per completed task.',
      compared: 'Compared with',
      comparedTitle: 'How to choose',
      practical: 'Practical tests',
      practicalTitle: 'Suggested real-world evaluations',
      caveats: 'Caveats',
      caveatsTitle: 'Read before routing production traffic',
      sources: 'Sources',
      sourceNote: 'GPTokens summarizes public model information for buyers. Confirm live model ID, price, context limits, and channel availability in Workspace before production launch.',
    },
    docs: {
      brand: 'GPTokens Docs',
      start: 'Start',
      core: 'Core',
      operations: 'Operations',
      onThisPage: 'On this page',
      overview: 'Overview',
      quickstart: 'Quickstart',
      models: 'Models',
      apiKeys: 'API Keys',
      requests: 'Requests',
      agents: 'Agent Setup',
      tokenSaving: 'Token Saving',
      billing: 'Billing',
      errors: 'Errors',
      usingApi: 'Using the GPTokens API',
      createKey: 'Create an account and key',
      sendRequests: 'Send OpenAI-compatible requests',
      configureAgents: 'Configure agents and CLIs',
      saveTokens: 'Save tokens in production',
      overviewBody: "GPTokens provides one OpenAI-compatible endpoint for China's top LLMs, with unified keys, routing, wallet credits, and usage tracking.",
      approach: 'Approach',
      bestFor: 'Best for',
      openaiSdk: 'OpenAI SDK',
      fastIntegration: 'Fast integration with existing apps',
      directApi: 'Direct API',
      fullControl: 'Full control from any language',
      agentClients: 'Agent clients',
      agentUse: 'Code agents, tool loops, and automation',
      getStarted: 'Get started',
      quickstartBody: 'The fastest path is the same pattern used by most API relay platforms: register, add balance, create a token, then replace the SDK endpoint.',
      auth: 'Authentication',
      apiKeyBody: 'Create keys from Workspace -> API Keys. Treat each key as a production secret.',
      recommendedKeys: 'Recommended key setup',
      envVars: 'Environment variables',
      apiReference: 'API reference',
      requestsBody: 'GPTokens currently prioritizes OpenAI-compatible chat and embeddings for MVP usage.',
      method: 'Method',
      path: 'Path',
      useCase: 'Use case',
      productionRules: 'Production request rules',
      modelSelection: 'Model selection',
      modelsTitle: 'Models and routing',
      modelsBody: 'Model availability depends on your account, region, plan, and provider health. Always call /v1/models for the live list before hard-coding names.',
      chineseLlmGuide: 'Chinese LLM API guide',
      chineseLlmGuideTitle: 'Use DeepSeek API, Qwen API, and other Chinese LLMs through one gateway',
      chineseLlmGuideBody: 'GPTokens is an independent OpenAI-compatible gateway for teams that want Chinese LLM API access without managing separate provider accounts. Use one API key to test DeepSeek, Qwen, Kimi, GLM, MiniMax, MiMo, and other routes, then compare model quality, pricing, latency, and reliability before scaling.',
      providerQuickstarts: 'Provider quickstarts',
      providerQuickstartsBody: 'These examples use GPTokens route names. Actual model availability can change, so check /v1/models before hard-coding any DeepSeek API, Qwen API, or Chinese LLM model name.',
      benchmarkGuideTitle: 'Chinese LLM list, benchmark, and leaderboard guidance',
      benchmarkGuideBody: 'Use this table as a practical Chinese LLM models list, then open each model detail page for benchmark notes and production caveats. Public Chinese LLM benchmark or leaderboard rankings are useful for shortlisting, but GPTokens recommends testing your own prompts for latency, tool-call reliability, refusal behavior, fallback rate, and cost per solved task.',
      provider: 'Provider',
      exampleModel: 'Example model',
      goodFor: 'Good for',
      agentTitle: 'Agents, coding tools, and CC Switch',
      agentBody: 'Most agent tools only need three values: endpoint, API key, and model. If the tool supports OpenAI-compatible providers, use the /v1 endpoint.',
      tool: 'Tool',
      endpoint: 'Endpoint',
      keySetting: 'Key setting',
      model: 'Model',
      notes: 'Notes',
      genericConfig: 'Generic OpenAI-compatible config',
      claudeConfig: 'Claude-compatible config',
      ccSwitchImport: 'CC Switch import',
      ccSwitchBody: 'Open Workspace -> API Keys, choose a key, and use the CC Switch action. GPTokens generates an import link with endpoint, key, model, provider name, and homepage metadata so you do not need to copy fields one by one.',
      agentGuidesEyebrow: 'Common agents',
      agentGuidesTitle: 'Agent-specific setup notes',
      agentGuidesBody: 'Use these notes for OpenClaw, Hermes, Codex, Claude Code, and similar coding agents. Endpoint choice depends on whether the client uses an OpenAI-compatible API or the Anthropic Messages API.',
      recommendedEndpoint: 'Recommended endpoint',
      recommendedModel: 'Recommended model',
      agentCautions: 'Notes and caveats',
      walletPlans: 'Wallet and plans',
      billingTitle: 'Billing, balance, and usage',
      billingBody: 'GPTokens is designed for users who want one payment account and one API key across multiple Chinese model providers.',
      costControl: 'Cost control',
      tokenSavingTitle: 'Token-saving playbook',
      tokenSavingBody: 'These practices are especially useful for coding agents, research agents, support bots, and document-heavy workflows.',
      troubleshooting: 'Troubleshooting',
      errorsTitle: 'Common API errors',
      errorsBody: 'Most integration issues come from key format, model names, account balance, provider availability, or rate limits.',
      status: 'Status',
      meaning: 'Meaning',
      fix: 'How to fix it',
    },
    auth: {
      signIn: 'Sign in',
      signInBody: 'Use your GPTokens account to manage API keys, billing, and model routing.',
      usernameEmail: 'Username or email',
      usernameEmailPlaceholder: 'Enter your username or email',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      continue: 'Continue',
      forgotPassword: 'Forgot password?',
      or: 'or',
      google: 'Continue with Google',
      github: 'Continue with GitHub',
      wechat: 'Continue with WeChat',
      noAccount: 'No account yet?',
      createOne: 'Create one',
      createAccount: 'Create account',
      createBody: 'Create one API key for DeepSeek, MiniMax, MiMo, Qwen, GLM, Kimi, and more Chinese models.',
      username: 'Username',
      usernamePlaceholder: 'Choose a username',
      newPasswordPlaceholder: 'At least 8 characters',
      confirmPassword: 'Confirm password',
      confirmPasswordPlaceholder: 'Repeat your password',
      email: 'Email',
      send: 'Send',
      verificationCode: 'Verification code',
      verificationPlaceholder: 'Enter the code',
      creating: 'Creating...',
      alreadyHave: 'Already have an account?',
      signInLink: 'Sign in',
      wechatTitle: 'Sign in with WeChat',
      wechatInstruction: 'Scan the QR code with WeChat, follow the account, and send "code" to receive a verification code. The code is valid for three minutes.',
    },
    backend: {
      welcome: 'Welcome to GPTokens',
      goodMorning: 'Good morning',
      goodAfternoon: 'Good afternoon',
      goodEvening: 'Good evening',
      settings: 'Settings',
      signOut: 'Sign out',
      language: 'Language',
      signedOut: 'Signed out',
      workspace: 'Workspace',
      admin: 'Admin',
      navDashboard: 'Dashboard',
      navApiKeys: 'API Keys',
      navBilling: 'Billing',
      navMembership: 'Membership',
      navLegacyBalance: 'Account balance',
      navMembershipRecovery: 'Membership recovery',
      membership_loading: 'Checking membership…',
      membership_inactive: 'No active membership',
      membership_unavailable: 'Membership unavailable',
      navUsage: 'Usage',
      navReferrals: 'Referrals',
      navAffiliate: 'Affiliate',
      navAnalytics: 'Analytics',
      navImages: 'Images',
      navSettings: 'Settings',
      navAdminDashboard: 'Admin Dashboard',
      navAdminSettings: 'Admin Settings',
      navChannels: 'Channels',
      navUsers: 'Users',
      navRedemptions: 'Redemptions',
      navLogs: 'Logs',
      dashboardTitle: 'Dashboard',
      dashboardSubtitle: "Unified model access, membership allowance, account balance, and usage.",
      adminEnabled: 'Admin controls enabled',
      customerWorkspace: 'Customer workspace',
      account: 'Account',
      balance: 'Balance',
      topUp: 'Top up',
      recharge: 'Recharge',
      setup: 'Set up',
      used: 'Used',
      usage: 'Usage',
      requests: 'Requests',
      billableCalls: 'Billable calls',
      membership: 'Membership',
      currentTier: 'Current plan',
      lifetimeRecharge: 'Historical recharge total {amount}',
      nextTierProgress: 'Review available membership plans',
      highestTier: 'Current subscription active',
      apiAccess: 'API Access',
      activeKeys: 'Active keys',
      totalKeys: '{count} total keys',
      availableModels: 'Available models',
      available: 'Available',
      on: 'On',
      off: 'Off',
      referralOverview: 'Referrals',
      referralLink: 'Referral link',
      ready: 'Ready',
      loading: 'Loading',
      copy: 'Copy',
      referralCopied: 'Referral link copied.',
      referralLinkPreparing: 'Preparing your referral link...',
      referralPairRewardBody: 'Historical invitation rewards are retained. Membership purchases do not automatically trigger the former recharge bonus.',
      successfulInvites: 'Successful invites',
      successfulInviteHelp: 'This count reflects historical qualifying invitations, not the number of membership purchases.',
      resources: 'Resources',
      subscriptionQuota: 'Subscription quota',
      tokens: 'Tokens',
      performance: 'Performance',
      avgRpm: 'Avg RPM',
      avgTpm: 'Avg TPM',
      cost: 'Cost',
      costSubtitle: 'Usage cost by model breakdown.',
      allModels: 'All models',
      allKeys: 'All keys',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      noUsageData: 'No usage data is available for the selected period.',
      usageHistory: 'Usage history',
      usageHistorySubtitle: 'Recent API usage and cost.',
      date: 'Date',
      model: 'Model',
      input: 'Input',
      output: 'Output',
      session: 'Session',
      walletPage: {
        redeemCredits: 'Redeem existing credits',
        redemptionCode: 'Redemption code',
        redeem: 'Redeem',
        rechargeBills: 'Historical recharge receipts',
        noRechargeBills: 'No historical recharge records yet.',
        billDate: 'Date',
        billPack: 'Historical item',
        billPaid: 'Paid',
        billBonus: 'Historical bonus',
        billStatus: 'Status',
        billActions: 'Actions',
        customAmount: 'Custom amount',
        pending: 'Pending',
        succeeded: 'Succeeded',
        failed: 'Failed',
        expired: 'Expired',
        cancelled: 'Cancelled',
        cancelPayment: 'Cancel unpaid checkout',
      },
      common: {
        userSettings: 'User Settings',
        userSettingsSubtitle: 'Manage profile, API access, referral link, and account security.',
      },
      personalSettings: {
        systemTokenReset: 'System access token reset and copied to clipboard.',
        systemTokenCopied: 'System access token copied to clipboard.',
        referralCopied: 'Referral link copied to clipboard.',
        referralRewardHint: 'Share your link to record invitations. View posted rewards and adjustments on the Referrals page.',
        confirmDeleteUsername: 'Enter your username to confirm account deletion.',
        accountDeleted: 'Account deleted.',
        wechatConnected: 'WeChat account connected.',
        passwordsMismatch: 'Passwords do not match.',
        passwordChanged: 'Password changed.',
        minimumTransfer: (amount) => `Minimum transfer amount is ${amount}`,
        enterEmail: 'Please enter your email address.',
        turnstileWait: 'Please wait a few seconds while Turnstile verifies your session.',
        verificationSent: 'Verification code sent. Please check your email.',
        enterEmailCode: 'Please enter the email verification code.',
        emailConnected: 'Email connected.',
        emailChanged: 'Email changed.',
        copiedPrefix: 'Copied: ',
        copyFailedTitle: 'Unable to copy to clipboard',
        transferTitle: 'Enter transfer amount',
        availableRewards: 'Available rewards',
        transferAmountMinimum: (amount) => `Transfer amount, minimum ${amount}`,
        admin: 'Admin',
        user: 'User',
        id: 'ID',
        balance: 'Balance',
        lifetimeUsage: 'Lifetime usage',
        requests: 'Requests',
        apiAccess: 'API access',
        availableModelsHint: 'Available models. Click a model name to copy it.',
        referralLink: 'Referral link',
        transfer: 'Transfer',
        profile: 'Profile',
        email: 'Email',
        wechat: 'WeChat',
        github: 'GitHub',
        notConnected: 'Not connected',
        connected: 'Connected',
        notEnabled: 'Not enabled',
        connect: 'Connect',
        changeEmail: 'Change email',
        connectEmail: 'Connect email',
        generateSystemToken: 'Generate system access token',
        changePassword: 'Change password',
        deleteAccount: 'Delete account',
        connectWechat: 'Connect WeChat account',
        wechatModalInstruction: 'Scan the QR code with WeChat, follow the account, and send "code" to receive a verification code. The code is valid for three minutes.',
        verificationCode: 'Verification code',
        connectEmailAddress: 'Connect email address',
        changeEmailAddress: 'Change email address',
        enterEmailAddress: 'Enter email address',
        resend: (countdown) => `Resend (${countdown})`,
        getCode: 'Get code',
        deleteWarning: 'You are deleting your account. All account data will be removed and cannot be recovered.',
        deletePlaceholder: (username) => `Enter your username ${username || ''} to confirm deletion`,
        newPassword: 'New password',
        confirmNewPassword: 'Confirm new password',
      },
      referralsPage: {
        title: 'Referrals',
        linkTitle: 'Referral link',
        active: 'Active',
        linkBody: () => 'Share this link to record invitations. View posted rewards and adjustments below.',
        copyLink: 'Copy link',
        refresh: 'Refresh',
        rewardTitle: 'Referral rewards',
        pageIntro: 'Share GPTokens. Earn together with your friends, then grow your commission rate.',
        shareLinkBody: 'Friends must register through your link so rewards can be attributed to you.',
        membershipLinkBody: 'Share your referral link to record invitations. Membership first-payment rewards settle after 14 days; wallet rewards qualify after $10 of confirmed consumption. Each invited user qualifies once.',
        membershipRewardBody: 'Referral rewards can be withdrawn separately. Membership and extra-pack commissions settle after 14 days; wallet commissions follow actual consumption.',
        rewardAmount: (amount) => `$${Number(amount).toFixed(2)} each`,
        rewardBody: (reward, threshold) => `Both sides receive $${Number(reward).toFixed(2)} after the invited user pays for an eligible membership or completes wallet consumption of at least $${Number(threshold).toFixed(2)}.`,
        successfulInvites: 'Successful invites',
        qualifyingRecharge: 'Wallet consumption threshold',
        recordsTitle: 'Referral records',
        date: 'Date',
        event: 'Event',
        amount: 'Amount',
        status: 'Status',
        inviterReward: 'Inviter reward',
        inviteeReward: 'Invitee reward',
        credited: 'Credited',
        noRewards: 'No invite rewards yet.',
        copied: 'Referral link copied.',
        copyFailed: 'Unable to copy referral link.'
      },
      affiliatePage: {
        title: 'Affiliate program',
        programTitle: 'Apply to become an affiliate',
        programBody: 'Apply here if you run a community, content channel, agency, or product that can bring GPTokens customers. Commission eligibility and rates follow your approved affiliate terms; membership allowance is not the commission amount.',
        bestFor: 'Best for',
        bestForValue: 'Creators, agencies, communities',
        commission: 'Commission',
        commissionValue: 'Reviewed by admin',
        review: 'Review',
        reviewValue: 'Manual approval',
        applicationTitle: 'Application',
        contactPlaceholder: 'Contact email, Telegram, or website',
        channelsPlaceholder: 'Promotion channels, e.g. YouTube, blog, Discord',
        audiencePlaceholder: 'Audience or customer profile',
        messagePlaceholder: 'Tell us how you plan to promote GPTokens',
        submit: 'Submit application',
        submitted: 'Affiliate application submitted.',
        adminRemark: 'Admin note',
        loginBody: 'Create an account or sign in to submit an affiliate application.',
        createAccount: 'Create account',
        signIn: 'Sign in',
        status: (status) => status || 'pending'
      },
      apiKeys: {
        title: 'API Keys',
        accessTitle: 'API Access',
        accessBody: 'Use this base URL with OpenAI-compatible SDKs.',
        keyBody: 'Create and manage API keys below. All keys share the account membership allowance. A key limit is an additional restriction; creating a new key does not reset weekly or monthly usage.',
        columns: {
          name: 'Name',
          status: 'Status',
          usedQuota: 'Used quota',
          remainingQuota: 'Remaining quota',
          createdTime: 'Created time',
          expiredTime: 'Expired time'
        },
        status: {
          enabledLimited: 'Enabled: model limits',
          enabled: 'Enabled',
          disabled: 'Disabled',
          expired: 'Expired',
          depleted: 'Depleted',
          unknown: 'Unknown'
        },
        unlimited: 'Unlimited',
        neverExpires: 'Never expires',
        view: 'View',
        copy: 'Copy',
        delete: 'Delete',
        disable: 'Disable',
        enable: 'Enable',
        edit: 'Edit',
        deleteTitle: 'Delete this API key?',
        deleteContent: 'This action cannot be undone.',
        copied: 'Copied to clipboard.',
        copyFailed: 'Unable to copy to clipboard. Please copy manually.',
        operationDone: 'Operation completed.',
        selectOne: 'Select at least one API key.',
        searchLabel: 'Search keyword',
        searchPlaceholder: 'API key name',
        search: 'Search',
        add: 'Add API key',
        copySelected: 'Copy selected API keys',
        defaultSort: 'Default sort',
        sortRemain: 'Sort by remaining quota',
        sortUsed: 'Sort by used quota',
        pageText: ({ currentStart, currentEnd, total }) => `${currentStart} - ${currentEnd} of ${total}`,
        ccSwitch: {
          title: 'Import to CC Switch',
          ok: 'Open CC Switch',
          cancel: 'Cancel',
          application: 'Application',
          providerName: 'Provider name',
          selectModel: 'Select model',
          missingModel: 'Select a primary model first.',
          note: 'This will open CC Switch with the selected provider, endpoint, API key, and model configuration.',
          fields: {
            'Primary model': 'Primary model',
            'Haiku model': 'Haiku model',
            'Sonnet model': 'Sonnet model',
            'Opus model': 'Opus model'
          }
        },
        editForm: {
          updateTitle: 'Update API key',
          createTitle: 'Create API key',
          submit: 'Submit',
          cancel: 'Cancel',
          name: 'Name',
          namePlaceholder: 'Enter a name',
          expiredTime: 'Expiration time',
          expiredPlaceholder: 'Choose expiration time',
          neverExpires: 'Never expires',
          oneHour: 'One hour',
          oneMonth: 'One month',
          oneDay: 'One day',
          quotaWarning: 'API key quota only limits this key. Actual usage is still limited by your account wallet balance.',
          quotaUsd: 'Quota (USD)',
          quotaPlaceholder: 'Enter USD quota, for example 1 or 0.5',
          unlimitedQuota: 'Unlimited quota',
          unlimitedQuotaHint: 'Recommended. The account wallet balance still controls actual spending.',
          count: 'Creation count',
          countLabel: 'Count',
          countPlaceholder: 'Select or enter the number of API keys to create',
          unlimitedOn: 'Set unlimited quota',
          unlimitedOff: 'Cancel unlimited quota',
          expiredFormatError: 'Expiration time format is invalid.',
          updateSuccess: 'API key updated.',
          createSuccess: (count) => `${count} API key${count > 1 ? 's' : ''} created. Copy keys from the list page.`,
          countOptions: (count) => `${count}`
        }
      },
      adminSettings: {
        title: 'Admin operation settings',
        subtitle: 'Website operations, referral, payment entry, ratios, login, email, and brand content are managed here.',
        growth: 'Referral and affiliate',
        billing: 'Billing and routing',
        access: 'Login and email',
        site: 'Site content'
      },
      cacheSettings: {
        title: 'Cache',
        diskTitle: 'Disk cache',
        diskBody: 'Stores large request bodies on disk to reduce retained memory. It does not cache model answers.',
        enableDisk: 'Enable disk cache',
        cachePath: 'Cache path',
        cachePathPlaceholder: 'Empty means system temp directory',
        threshold: 'Threshold MB',
        maxSize: 'Max size MB',
        promptTitle: 'Prompt cache billing',
        promptBody: 'Applies lower billing to cached input tokens reported by upstream providers.',
        readRatio: 'Read ratio',
        creationRatio: 'Creation ratio',
        diskFiles: 'Disk files',
        diskUsage: 'Disk usage',
        memoryBuffers: 'Memory buffers',
        memoryUsage: 'Memory usage',
        diskHits: 'Disk hits',
        memoryHits: 'Memory hits',
        promptCacheHit: 'Prompt cache hit',
        estimatedSaved: 'Estimated saved',
        tokens: 'tokens',
        reload: 'Reload',
        resetStats: 'Reset cache stats',
        cleanDisk: 'Clean expired disk cache',
        save: 'Save cache settings',
        saved: 'Cache settings saved.',
        resetDone: 'Cache stats reset.',
        cleaned: (count) => `Cleaned ${count} disk cache files.`
      },
      business: {
        loadFailed: 'Admin dashboard failed to load',
        period: 'Period',
        sales: 'Sales',
        estimatedCost: 'Estimated cost',
        estimatedProfit: 'Estimated gross profit',
        today: 'Today',
        sevenDays: '7 days',
        thirtyDays: '30 days',
        title: 'Admin Dashboard',
        subtitle: 'Manage users, revenue, channels, model traffic, and operations in one place.',
        channelManagement: 'Channel management',
        refresh: 'Refresh data',
        totalUsers: 'Total users',
        totalUsersBody: 'Includes regular users and administrator accounts',
        thirtyDaySales: '30-day sales',
        salesBody: 'Converted from gateway quota to USD',
        availableChannels: 'Available channels',
        channelSummary: (disabled, lowBalance) => `${disabled} unavailable, ${lowBalance} low balance`,
        estimatedCostRate: 'Estimated cost rate',
        costRateBody: 'Adjustable in operation settings',
        salesAndCost: 'Sales and cost',
        channelStatus: 'Channel status',
        manageChannels: 'Manage channels',
        channel: 'Channel',
        provider: 'Provider',
        status: 'Status',
        remainingQuota: 'Remaining quota',
        usedQuota: 'Used quota',
        response: 'Response',
        modelRanking: 'Model usage ranking / 30 days',
        emptyModelUsage: 'No usage data',
        model: 'Model',
        requests: 'Requests',
        available: 'Available',
        disabled: 'Disabled',
        autoBanned: 'Auto banned',
        unknown: 'Unknown'
      },
    },
  },
  zh: {
    common: {
      all: '全部',
      loading: '加载中…',
      loadingNamed: '正在加载{name}…',
      details: '详情',
      viewDetails: '查看详情',
      backToModels: '返回模型列表',
      tokens: 'tokens',
      errors: {
        prefix: '错误：',
        network: '网络连接中断，请先确认操作状态，再决定是否重试。',
        forbidden: '你没有权限执行此操作。',
        method: '不支持此请求方式。',
        rateLimit: '请求次数过多，请稍后继续。',
        server: '服务器未能完成操作，请先确认操作状态，再决定是否重试。',
        unavailable: '服务暂不可用。',
        unknown: '发生未知错误。',
      },
    },
    nav: {
      models: '模型',
      agents: 'Agents',
      price: '价格',
      affiliate: 'Affiliate',
      docs: '文档',
      workspace: '工作台',
      login: '登录',
      privacy: '隐私政策',
      terms: '服务条款',
      support: '技术支持',
    },
    home: {
      title: '一个 API，调用中国顶级大模型',
      subtitle: '90% 的优秀体验，10% 的使用成本',
      getApiKey: '获取 API Key',
      exploreModels: '查看模型',
      providerTitle: '深度集成主流模型供应商',
      workflowLabel: '使用流程',
      workflowTitle: '三步接入生产环境',
      faqLabel: '常见问题',
      faqTitle: '购买前你可能想知道',
      faqIntro: '面向正在比较中国模型 API、OpenAI 兼容网关和低成本 GPT 替代方案的团队。',
      collapse: '收起答案',
      expand: '展开答案',
      painCards: [
        {
          visual: 'cost',
          title: '海外顶级模型成本太高',
          body: '中国前沿模型在对话、代码、Agent 和翻译场景已经很强，同时 token 成本更低。'
        },
        {
          visual: 'access',
          title: '中国 API 接入门槛高',
          body: 'GPTokens 提供英文文档、统一账户和全球支付，减少语言、付款和供应商账号的摩擦。'
        },
        {
          visual: 'route',
          title: '多个账号和订阅太麻烦',
          body: '在一个工作台访问已支持模型，所有 API Key 共享同一份会员额度和周限制。'
        }
      ],
      steps: [
        ['注册并创建 API Key', '注册账号，进入工作台，为你的应用或 Agent 创建平台 API Key。'],
        ['选择会员套餐', 'Plus 每月 $9.90 含 $20 额度，Pro $39 含 $100，Max $99 含 $300（是 Plus 的 15 倍）。购买前查看模型倍率和周限制。'],
        ['配置 URL 并开始调用', '设置 GPTokens Base URL 和 API Key，然后从模型列表中任选一个模型开始使用。']
      ],
      faqs: [
        ['每周重置会增加月额度吗？', '不会。周基础上限是月额度的一半：Plus 10、Pro 50、Max 150。周重置只恢复本周可用空间，不增加月额度。'],
        ['Max 加油包如何使用？', 'Max 基础周额度或月额度用完后，符合条件的会员可用 $25 购买 75 额度（Max 月额度的 25%）。加油包在同一账期内跨周保留，账期结束失效，不延长订阅，也不自动续购。'],
        ['支持哪些支付方式？', '可用支付方式以结账页面为准，取决于地区、币种和支付服务配置。会员是否开放购买请查看价格页。'],
        ['我在中国以外如何获得 DeepSeek API Key？', '注册 GPTokens、查看会员购买状态并创建一个 GPTokens API Key，就可以通过 GPTokens 调用 DeepSeek 模型。GPTokens 是独立网关，不是 DeepSeek 官方 API Key 发行方。'],
        ['Qwen API 可以配合 OpenAI-compatible SDK 使用吗？', '可以。把 SDK 的 base URL 改为 GPTokens endpoint，然后从实时模型列表选择 qwen3.7-plus 等 Qwen 路由。'],
        ['哪种中国 LLM API 更适合代码任务？', '代码任务可以从 DeepSeek、Qwen、Kimi、MiniMax 或 GLM 路由开始测试，重点比较答案通过率、延迟、工具调用可靠性和单任务成本。GPTokens 让这些模型共用一个 API Key。'],
        ['GPTokens 提供中国 LLM 模型列表吗？', '提供。Models 页面和 /v1/models endpoint 会列出已支持的中国 LLM 模型路由，包括 DeepSeek、Qwen、Kimi、GLM、MiniMax、MiMo 等。'],
        ['如何比较 DeepSeek、Qwen、Kimi、GLM 和 MiniMax 的 benchmark？', '公开中国 LLM benchmark 和 leaderboard 适合做初筛。生产前仍应使用自己的 prompts 测试延迟、准确率、拒答、工具调用和最终成本。'],
        ['一个 API Key 能调用所有支持的中国模型吗？', '可以。一个 GPTokens API Key 可以路由到 DeepSeek、MiniMax、MiMo、Qwen、GLM、Kimi 等已支持供应商。'],
        ['价格是如何计算的？', '模型价格按官方原价，不收取额外费用，按模型倍率扣减会员额度。Plus、Pro、Max 按账户共享月额度和周上限，具体规则与购买状态见价格页。'],
        ['GPTokens 兼容 OpenAI SDK 吗？', '兼容。首个公开协议就是 OpenAI-compatible，大多数 SDK 只需要替换 base URL、模型名和 API Key。']
      ],
    },
    pricing: {
      kicker: '月度会员',
      title: '价格',
      subtitle: '选择 Plus、Pro 或 Max 月度额度。所有模型基础价格与 OpenCode 一致，并按模型公开倍率扣减额度。',
      topUp: '会员',
      paymentAmount: '月付价格',
      bonusCredits: '月额度',
      walletCredits: '账户余额',
      bonusRate: '模型倍率',
      automatic: '自动计算',
      topUpNow: '查看套餐',
      startBuilding: '开始使用',
      membershipLabel: '会员体系',
      membershipTitle: '所有套餐使用相同的模型基础单价',
      membershipBody: '套餐决定月额度和周上限；模型倍率决定一次请求消耗多少会员额度。',
      tier: '套餐',
      cumulative: '月度额度',
      modelMultiplier: '模型倍率',
      benefit: '权益',
      calculatorLabel: '额度估算器',
      calculatorTitle: '估算套餐可支持的请求量',
      calculatorBody: '按模型基础价格和已发布会员倍率估算。',
      userState: '用户状态',
      purchase: '套餐',
      paid: '支付',
      bonus: '额度',
      effectiveCost: '额度消耗',
      tokenExample: 'Token 示例',
    },
    checkout: {
      title: '支付',
      rechargePackage: '充值套餐',
      recharge: '充值',
      rechargeSubtitle: '一次性预付钱包额度',
      custom: '自定义',
      customAmount: '自定义充值金额',
      paymentAmount: '支付金额',
      bonusCredits: '赠送额度',
      totalCredits: '到账钱包额度',
      bonusRate: '赠送比例',
      packMultiplier: '套餐倍率',
      bonusType: '赠送类型',
      payment: '支付方式',
      paymentBody: 'Stripe 会根据你的地区、币种、设备和我们 Stripe 账户的配置，最终显示可用的支付方式。Stripe 确认付款后，额度会自动到账。',
      recommendedCheckout: '推荐支付',
      recommendedCheckoutNote: '继续进入 Stripe Checkout，它会显示当前对你可用的支付方式。',
      recommendedGlobally: '全球推荐',
      availableRegion: '你所在地区可用',
      noRegionDetected: '当前会话没有识别到地区支付方式。进入 Stripe 后仍可能显示本地支付选项。',
      showMore: '显示更多支付方式',
      hideMore: '收起更多支付方式',
      moreMethods: '更多支付方式',
      continuePayment: '继续支付',
      invalidAmount: '请先填写有效的充值金额。',
      updatingPreview: '正在更新充值预览...',
      bonusTypes: {
        top_up_pack: '充值包赠送',
        none: '无',
      },
      paymentGroups: {
        global: '全球推荐',
        regional: '你所在地区可用',
        more: '更多支付方式',
      },
      paymentMethods: {
        card: { name: '银行卡', note: '通常可通过 Stripe Checkout 使用。' },
        apple_google_pay: { name: 'Apple Pay / Google Pay', note: 'Stripe 会在支持的设备和浏览器中显示。' },
        link: { name: 'Link', note: '可用时显示 Stripe 快速钱包支付。' },
        amazon_pay: { name: 'Amazon Pay', note: '通常可通过 Stripe Checkout 使用。' },
        alipay: { name: '支付宝', note: '适合中国及跨境买家常用的钱包。' },
        wechat_pay: { name: '微信支付', note: '适合中国及跨境买家常用的钱包。' },
        cartes_bancaires: { name: 'Cartes Bancaires', note: '可能需要法国本地卡资格。' },
        kakao_pay: { name: 'Kakao Pay', note: '可能需要韩国本地账号资格。' },
        naver_pay: { name: 'Naver Pay', note: '可能需要韩国本地账号资格。' },
        payco: { name: 'PAYCO', note: '可能需要韩国本地账号资格。' },
        samsung_pay: { name: 'Samsung Pay', note: '可能需要本地设备或账号资格。' },
        mb_way: { name: 'MB WAY', note: '可能需要葡萄牙本地账号资格。' },
        revolut_pay: { name: 'Revolut Pay', note: '可能需要本地账号资格。' },
        satispay: { name: 'Satispay', note: '可能需要意大利本地账号资格。' },
        bancontact: { name: 'Bancontact', note: '可能需要比利时本地银行资格。' },
        blik: { name: 'BLIK', note: '可能需要波兰本地银行资格。' },
        eps: { name: 'EPS', note: '可能需要奥地利本地银行资格。' },
        pix: { name: 'Pix', note: '可能需要巴西本地账号资格。' },
      },
    },
    checkoutSuccess: {
      missingSession: '缺少支付会话 ID。',
      notFound: '没有找到这个支付会话。如果你刚完成支付，请进入钱包查看充值账单。',
      paid: '支付金额',
      bonus: '赠送额度',
      total: '到账钱包额度',
      statusLabel: '状态',
      wallet: '进入钱包',
      refresh: '刷新状态',
      note: 'Stripe 通知 GPTokens 可能需要几秒钟。支付待确认时，本页面会自动刷新状态。',
      status: {
        pending: {
          title: '正在确认支付',
          body: 'Stripe 已经把你带回 GPTokens。我们正在等待支付确认通知，确认后会自动加余额。',
          label: '待确认'
        },
        succeeded: {
          title: '支付成功',
          body: '钱包额度已经到账，你现在可以用 API Key 调用了。',
          label: '已成功'
        },
        expired: {
          title: '支付会话已过期',
          body: '本次没有加余额。准备付款时请重新发起 checkout。',
          label: '已过期'
        },
        failed: {
          title: '支付未完成',
          body: '本次没有加余额。请重新支付或换一个支付方式。',
          label: '失败'
        },
      },
    },
    models: {
      search: '搜索模型、供应商、代码、智能体...',
      supported: '个模型',
      notFound: '未找到模型',
      notFoundTitle: '这个模型暂未收录到 GPTokens 模型目录。',
      notFoundBody: '请返回模型列表，选择当前已有介绍的模型。',
      allModels: '全部模型',
      apiName: 'API 名称',
      context: '上下文',
      modality: '模态',
      source: '来源',
      overview: '概览',
      overviewTitle: '这个模型适合做什么',
      capabilities: '核心能力',
      capabilitiesTitle: '适用场景',
      benchmarks: '跑分',
      benchmarksTitle: '公开跑分快照',
      benchmark: '榜单/测试',
      result: '结果',
      benchmarkSource: '来源',
      benchmarkNote: '跑分只适合做初筛信号，不代表生产表现。上线前仍需测试延迟、工具调用可靠性、拒答行为、兜底率和单任务成本。',
      compared: '同类对比',
      comparedTitle: '如何选择',
      practical: '实操测试',
      practicalTitle: '建议做的真实项目测试',
      caveats: '注意事项',
      caveatsTitle: '接入生产流量前请阅读',
      sources: '资料来源',
      sourceNote: 'GPTokens 会整理公开模型资料，方便购买前判断。生产前请在工作台确认实时模型 ID、价格、上下文长度和渠道可用性。',
    },
    docs: {
      brand: 'GPTokens 文档',
      start: '开始',
      core: '核心',
      operations: '运营',
      onThisPage: '本页目录',
      overview: '概览',
      quickstart: '快速开始',
      models: '模型',
      apiKeys: 'API Keys',
      requests: '请求',
      agents: '智能体配置',
      tokenSaving: '节省 Token',
      billing: '计费',
      errors: '错误处理',
      usingApi: '使用 GPTokens API',
      createKey: '创建账号和 API Key',
      sendRequests: '发送 OpenAI 兼容请求',
      configureAgents: '配置智能体和 CLI',
      saveTokens: '在生产中节省 Token',
      overviewBody: 'GPTokens 为中国顶级大模型提供一个 OpenAI 兼容入口，包含统一 API Key、智能路由、钱包额度和用量追踪。',
      approach: '接入方式',
      bestFor: '适合场景',
      openaiSdk: 'OpenAI SDK',
      fastIntegration: '快速迁移已有应用',
      directApi: '直接 API',
      fullControl: '任意语言完全控制请求',
      agentClients: '智能体客户端',
      agentUse: '代码智能体、工具循环和自动化任务',
      getStarted: '开始使用',
      quickstartBody: '最快的方式和多数 API 中转平台一样：注册账号、添加余额、创建 API Key，然后替换 SDK endpoint。',
      auth: '鉴权',
      apiKeyBody: '在工作台的 API Keys 页面创建密钥。请把每个 key 当成生产密钥保管。',
      recommendedKeys: '推荐 Key 管理方式',
      envVars: '环境变量',
      apiReference: 'API 参考',
      requestsBody: 'GPTokens 当前优先支持 OpenAI 兼容的 chat 和 embeddings。',
      method: '方法',
      path: '路径',
      useCase: '用途',
      productionRules: '生产请求规则',
      modelSelection: '模型选择',
      modelsTitle: '模型与路由',
      modelsBody: '模型可用性取决于账号、地区、套餐和供应商健康状态。生产前请调用 /v1/models 获取实时列表。',
      chineseLlmGuide: '中国 LLM API 指南',
      chineseLlmGuideTitle: '通过一个网关调用 DeepSeek API、Qwen API 和其他中国 LLM',
      chineseLlmGuideBody: 'GPTokens 是独立的 OpenAI 兼容网关，适合不想分别管理多个供应商账号的团队。你可以用一个 API Key 测试 DeepSeek、Qwen、Kimi、GLM、MiniMax、MiMo 等路由，再比较模型质量、价格、延迟和稳定性。按照英文站点保持一致，这里不声明 GPTokens 是任何供应商官方服务。',
      providerQuickstarts: '供应商快速接入',
      providerQuickstartsBody: '这些示例使用 GPTokens 路由名。实际可用模型可能变化，生产前请通过 /v1/models 确认 DeepSeek API、Qwen API 或其他中国 LLM 模型名。',
      benchmarkGuideTitle: '中国 LLM 列表、benchmark 和 leaderboard 说明',
      benchmarkGuideBody: '这个表格可以作为实用的中国 LLM 模型列表。打开每个模型详情页可以查看 benchmark 备注和生产注意事项。公开中国 LLM benchmark 或 leaderboard 适合做初筛，但 GPTokens 建议用自己的 prompts 测试延迟、工具调用可靠性、拒答、兜底率和单任务成本。',
      provider: '供应商',
      exampleModel: '示例模型',
      goodFor: '适合',
      agentTitle: '智能体、代码工具和 CC Switch',
      agentBody: '大多数智能体工具只需要 endpoint、API Key 和 model。只要支持 OpenAI-compatible provider，就使用 /v1 endpoint。',
      tool: '工具',
      endpoint: 'Endpoint',
      keySetting: 'Key 设置',
      model: '模型',
      notes: '说明',
      genericConfig: '通用 OpenAI 兼容配置',
      claudeConfig: 'Claude 兼容配置',
      ccSwitchImport: 'CC Switch 导入',
      ccSwitchBody: '打开工作台 -> API Keys，选择一个 key，然后使用 CC Switch 操作。GPTokens 会生成包含 endpoint、key、model、provider name 和主页元数据的导入链接。',
      agentGuidesEyebrow: '常用智能体',
      agentGuidesTitle: '常用 Agent 专项配置说明',
      agentGuidesBody: '以下说明适用于 OpenClaw、Hermes、Codex、Claude Code 和类似代码智能体。Endpoint 取决于客户端使用 OpenAI-compatible API 还是 Anthropic Messages API。',
      recommendedEndpoint: '推荐 Endpoint',
      recommendedModel: '推荐模型',
      agentCautions: '注意事项',
      walletPlans: '钱包与套餐',
      billingTitle: '计费、余额和用量',
      billingBody: 'GPTokens 适合想用一个支付账号和一个 API Key 调用多个中国模型供应商的用户。',
      costControl: '成本控制',
      tokenSavingTitle: '节省 Token 指南',
      tokenSavingBody: '这些做法尤其适合代码智能体、研究智能体、客服机器人和文档密集型工作流。',
      troubleshooting: '排障',
      errorsTitle: '常见 API 错误',
      errorsBody: '大多数接入问题来自 key 格式、模型名、账户余额、供应商可用性或速率限制。',
      status: '状态',
      meaning: '含义',
      fix: '解决方式',
    },
    auth: {
      signIn: '登录',
      signInBody: '使用 GPTokens 账号管理 API Key、账单和模型路由。',
      usernameEmail: '用户名或邮箱',
      usernameEmailPlaceholder: '输入用户名或邮箱',
      password: '密码',
      passwordPlaceholder: '输入密码',
      showPassword: '显示密码',
      hidePassword: '隐藏密码',
      continue: '继续',
      forgotPassword: '忘记密码？',
      or: '或',
      google: '使用 Google 继续',
      github: '使用 GitHub 继续',
      wechat: '使用微信继续',
      noAccount: '还没有账号？',
      createOne: '创建账号',
      createAccount: '创建账号',
      createBody: '创建一个 API Key，即可调用 DeepSeek、MiniMax、MiMo、Qwen、GLM、Kimi 等中国模型。',
      username: '用户名',
      usernamePlaceholder: '设置用户名',
      newPasswordPlaceholder: '至少 8 个字符',
      confirmPassword: '确认密码',
      confirmPasswordPlaceholder: '再次输入密码',
      email: '邮箱',
      send: '发送',
      verificationCode: '验证码',
      verificationPlaceholder: '输入验证码',
      creating: '创建中...',
      alreadyHave: '已有账号？',
      signInLink: '登录',
      wechatTitle: '微信登录',
      wechatInstruction: '使用微信扫描二维码，关注账号并发送 “code” 获取验证码。验证码三分钟内有效。',
    },
    backend: {
      welcome: '欢迎使用 GPTokens',
      goodMorning: '早上好',
      goodAfternoon: '下午好',
      goodEvening: '晚上好',
      settings: '设置',
      signOut: '退出登录',
      language: '语言',
      signedOut: '已退出登录',
      workspace: '工作台',
      admin: '管理员',
      navDashboard: '看板',
      navApiKeys: 'API Keys',
      navBilling: '钱包',
      navMembership: '会员与账单',
      navLegacyBalance: '账户余额',
      navMembershipRecovery: '会员异常处理',
      membership_loading: '正在查询会员…',
      membership_inactive: '暂无有效会员',
      membership_unavailable: '会员状态暂不可用',
      navUsage: '用量',
      navReferrals: '推广',
      navAffiliate: 'Affiliate 申请',
      navAnalytics: '分析',
      navImages: '绘图',
      navSettings: '设置',
      navAdminDashboard: '管理员看板',
      navAdminSettings: '管理员设置',
      navChannels: '渠道',
      navUsers: '用户',
      navRedemptions: '兑换码',
      navLogs: '日志',
      dashboardTitle: '看板',
      dashboardSubtitle: '会员周/月额度、按量付费余额和用量总览。',
      adminEnabled: '管理员功能已启用',
      customerWorkspace: '用户工作台',
      account: '账户',
      balance: '余额',
      topUp: '充值',
      recharge: '充值',
      setup: '设置',
      used: '已用',
      usage: '用量',
      requests: '请求数',
      billableCalls: '计费调用',
      membership: '会员',
      currentTier: '当前套餐',
      lifetimeRecharge: '历史充值合计 {amount}',
      nextTierProgress: '查看可用会员套餐',
      highestTier: '当前订阅有效',
      apiAccess: 'API 接入',
      activeKeys: '启用 Key',
      totalKeys: '共 {count} 个 Key',
      availableModels: '可用模型',
      available: '可用',
      on: '开启',
      off: '关闭',
      referralOverview: '推广情况',
      referralLink: '推广链接',
      ready: '已生成',
      loading: '加载中',
      copy: '复制',
      referralCopied: '推广链接已复制。',
      referralLinkPreparing: '正在生成推广链接...',
      referralPairRewardBody: '会员付款满 14 天、余额实际消费满 $10 后满足首次奖励条件，两条路径只奖励一次。',
      successfulInvites: '成功邀请',
      successfulInviteHelp: '此处为历史有效邀请数量，不代表会员购买人数。',
      resources: '资源',
      subscriptionQuota: '订阅额度',
      tokens: 'Tokens',
      performance: '性能',
      avgRpm: '平均 RPM',
      avgTpm: '平均 TPM',
      cost: '成本',
      costSubtitle: '按模型拆分的用量成本。',
      allModels: '全部模型',
      allKeys: '全部 Key',
      previousMonth: '上一个月',
      nextMonth: '下一个月',
      noUsageData: '所选期间无可用使用数据。',
      usageHistory: '用量历史',
      usageHistorySubtitle: '最近 API 用量和成本。',
      date: '日期',
      model: '模型',
      input: '输入',
      output: '输出',
      session: '会话',
      walletPage: {
        redeemCredits: '兑换旧额度',
        redemptionCode: '兑换码',
        redeem: '兑换',
        rechargeBills: '历史充值账单',
        noRechargeBills: '暂无历史充值记录。',
        billDate: '日期',
        billPack: '历史商品',
        billPaid: '支付金额',
        billBonus: '历史赠送',
        billStatus: '状态',
        billActions: '操作',
        customAmount: '自定义金额',
        pending: '待支付',
        succeeded: '成功',
        failed: '失败',
        expired: '已过期',
        cancelled: '已取消',
        cancelPayment: '取消未付款订单',
      },
      common: {
        userSettings: '用户设置',
        userSettingsSubtitle: '管理个人资料、API 接入、推广链接和账户安全。',
      },
      personalSettings: {
        systemTokenReset: '系统访问令牌已重置并复制到剪贴板。',
        systemTokenCopied: '系统访问令牌已复制到剪贴板。',
        referralCopied: '推广链接已复制到剪贴板。',
        referralRewardHint: '分享链接可记录邀请关系，已入账奖励及调整请在推广页查看。',
        confirmDeleteUsername: '请输入用户名以确认删除账户。',
        accountDeleted: '账户已删除。',
        wechatConnected: '微信账号已绑定。',
        passwordsMismatch: '两次输入的密码不一致。',
        passwordChanged: '密码已修改。',
        minimumTransfer: (amount) => `最低转入金额为 ${amount}`,
        enterEmail: '请输入邮箱地址。',
        turnstileWait: '请稍等几秒，Turnstile 正在验证当前会话。',
        verificationSent: '验证码已发送，请检查邮箱。',
        enterEmailCode: '请输入邮箱验证码。',
        emailConnected: '邮箱已绑定。',
        emailChanged: '邮箱已修改。',
        copiedPrefix: '已复制：',
        copyFailedTitle: '无法复制到剪贴板',
        transferTitle: '输入转入金额',
        availableRewards: '可用推广奖励',
        transferAmountMinimum: (amount) => `转入金额，最低 ${amount}`,
        admin: '管理员',
        user: '用户',
        id: 'ID',
        balance: '余额',
        lifetimeUsage: '累计用量',
        requests: '请求数',
        apiAccess: 'API 接入',
        availableModelsHint: '当前可用模型。点击模型名即可复制。',
        referralLink: '推广链接',
        transfer: '转入余额',
        profile: '个人资料',
        email: '邮箱',
        wechat: '微信',
        github: 'GitHub',
        notConnected: '未绑定',
        connected: '已绑定',
        notEnabled: '未启用',
        connect: '绑定',
        changeEmail: '修改邮箱',
        connectEmail: '绑定邮箱',
        generateSystemToken: '生成系统访问令牌',
        changePassword: '修改密码',
        deleteAccount: '删除账户',
        connectWechat: '绑定微信账号',
        wechatModalInstruction: '使用微信扫描二维码，关注账号并发送 “code” 获取验证码。验证码三分钟内有效。',
        verificationCode: '验证码',
        connectEmailAddress: '绑定邮箱地址',
        changeEmailAddress: '修改邮箱地址',
        enterEmailAddress: '输入邮箱地址',
        resend: (countdown) => `重新发送（${countdown}）`,
        getCode: '获取验证码',
        deleteWarning: '你正在删除账户。所有账户数据都会被移除，且无法恢复。',
        deletePlaceholder: (username) => `输入用户名 ${username || ''} 以确认删除`,
        newPassword: '新密码',
        confirmNewPassword: '确认新密码',
      },
      referralsPage: {
        title: '推广',
        linkTitle: '推广链接',
        active: '启用',
        linkBody: () => '分享链接可记录邀请关系，已入账奖励及调整请在下方查看。',
        copyLink: '复制链接',
        refresh: '刷新',
        rewardTitle: '推荐奖励',
        pageIntro: '分享 GPTokens，与好友一起领首次奖励，持续推荐解锁更高佣金。',
        shareLinkBody: '好友通过专属链接注册后，推荐关系才会计入你的账户。',
        membershipLinkBody: '分享推广链接记录邀请关系。会员付款满 14 天后结算；余额实际消费满 $10 后满足首次奖励条件，两条路径只奖励一次。',
        membershipRewardBody: '推荐奖励独立提现。会员和加油包佣金在付款满 14 天后结算；余额佣金按实际消费计算。',
        rewardAmount: (amount) => `双方各 $${Number(amount).toFixed(2)}`,
        rewardBody: (reward, threshold) => `被邀请用户完成有效会员付款或实际余额消费累计达到 $${Number(threshold).toFixed(2)} 后，双方各获得 $${Number(reward).toFixed(2)} 推荐奖励。`,
        successfulInvites: '成功邀请',
        qualifyingRecharge: '余额消费门槛',
        recordsTitle: '推广记录',
        date: '日期',
        event: '事件',
        amount: '金额',
        status: '状态',
        inviterReward: '邀请人奖励',
        inviteeReward: '被邀请人奖励',
        credited: '已到账',
        noRewards: '暂无邀请奖励记录。',
        copied: '推广链接已复制。',
        copyFailed: '无法复制推广链接。'
      },
      affiliatePage: {
        title: 'Affiliate 申请',
        programTitle: '申请成为推广员',
        programBody: '如果你有社区、内容渠道、代理资源或产品流量，可以申请 Affiliate 分成。适用订单和比例以已批准的推广协议为准，会员额度不是佣金金额。',
        bestFor: '适合人群',
        bestForValue: '创作者、代理、社区',
        commission: '分成比例',
        commissionValue: '管理员审核设置',
        review: '审核方式',
        reviewValue: '人工审核',
        applicationTitle: '申请表',
        contactPlaceholder: '联系邮箱、Telegram 或网站',
        channelsPlaceholder: '推广渠道，例如 YouTube、博客、Discord',
        audiencePlaceholder: '受众或客户类型',
        messagePlaceholder: '说明你计划如何推广 GPTokens',
        submit: '提交申请',
        submitted: 'Affiliate 申请已提交。',
        adminRemark: '管理员备注',
        loginBody: '请先注册或登录账号，然后提交 Affiliate 申请。',
        createAccount: '注册账号',
        signIn: '登录',
        status: (status) => {
          if (status === 'approved') return '已通过';
          if (status === 'rejected') return '已拒绝';
          return '待审核';
        }
      },
      apiKeys: {
        title: 'API Keys',
        accessTitle: 'API 接入',
        accessBody: '在 OpenAI 兼容 SDK 中使用这个 base URL。',
        keyBody: '在下方创建和管理 API Key。所有 Key 共享账户会员额度；Key 限额是额外限制，创建新 Key 不会重置周用量或月用量。',
        columns: {
          name: '名称',
          status: '状态',
          usedQuota: '已用额度',
          remainingQuota: '剩余额度',
          createdTime: '创建时间',
          expiredTime: '过期时间'
        },
        status: {
          enabledLimited: '已启用：限制模型',
          enabled: '已启用',
          disabled: '已禁用',
          expired: '已过期',
          depleted: '已耗尽',
          unknown: '未知状态'
        },
        unlimited: '无限制',
        neverExpires: '永不过期',
        view: '查看',
        copy: '复制',
        delete: '删除',
        disable: '禁用',
        enable: '启用',
        edit: '编辑',
        deleteTitle: '确定要删除这个 API Key？',
        deleteContent: '此操作不可撤销。',
        copied: '已复制到剪贴板。',
        copyFailed: '无法复制到剪贴板，请手动复制。',
        operationDone: '操作成功完成。',
        selectOne: '请至少选择一个 API Key。',
        searchLabel: '搜索关键词',
        searchPlaceholder: 'API Key 名称',
        search: '查询',
        add: '添加 API Key',
        copySelected: '复制所选 API Key 到剪贴板',
        defaultSort: '默认排序',
        sortRemain: '按剩余额度排序',
        sortUsed: '按已用额度排序',
        pageText: ({ currentStart, currentEnd, total }) => `第 ${currentStart} - ${currentEnd} 条，共 ${total} 条`,
        ccSwitch: {
          title: '导入到 CC Switch',
          ok: '打开 CC Switch',
          cancel: '取消',
          application: '应用',
          providerName: '供应商名称',
          selectModel: '选择模型',
          missingModel: '请先选择主模型。',
          note: '这会打开 CC Switch，并带入所选供应商、endpoint、API Key 和模型配置。',
          fields: {
            'Primary model': '主模型',
            'Haiku model': 'Haiku 模型',
            'Sonnet model': 'Sonnet 模型',
            'Opus model': 'Opus 模型'
          }
        },
        editForm: {
          updateTitle: '更新 API Key',
          createTitle: '创建 API Key',
          submit: '提交',
          cancel: '取消',
          name: '名称',
          namePlaceholder: '请输入名称',
          expiredTime: '过期时间',
          expiredPlaceholder: '请选择过期时间',
          neverExpires: '永不过期',
          oneHour: '一小时',
          oneMonth: '一个月',
          oneDay: '一天',
          quotaWarning: 'API Key 的额度仅用于限制这个 key 本身的最大使用量，实际使用仍受账号钱包余额限制。',
          quotaUsd: '额度（USD）',
          quotaPlaceholder: '请输入美元额度，例如 1 或 0.5',
          unlimitedQuota: '无限额度',
          unlimitedQuotaHint: '推荐开启。实际可用金额仍由账号钱包余额控制。',
          count: '新建数量',
          countLabel: '数量',
          countPlaceholder: '请选择或输入创建 API Key 的数量',
          unlimitedOn: '设为无限额度',
          unlimitedOff: '取消无限额度',
          expiredFormatError: '过期时间格式错误。',
          updateSuccess: 'API Key 更新成功。',
          createSuccess: (count) => `${count} 个 API Key 创建成功，请在列表页面点击复制获取 key。`,
          countOptions: (count) => `${count} 个`
        }
      },
      adminSettings: {
        title: '管理员运营设置',
        subtitle: '网站运营、推广、支付入口、倍率、登录、邮件和品牌内容都在这里统一配置。',
        growth: '推广与代理',
        billing: '计费与路由',
        access: '登录与邮件',
        site: '网站内容'
      },
      cacheSettings: {
        title: '缓存',
        diskTitle: '磁盘缓存',
        diskBody: '把较大的请求体临时写入磁盘，降低内存占用。不会缓存模型回答内容。',
        enableDisk: '启用磁盘缓存',
        cachePath: '缓存目录',
        cachePathPlaceholder: '留空表示使用系统临时目录',
        threshold: '触发阈值 MB',
        maxSize: '最大占用 MB',
        promptTitle: 'Prompt Cache 计费',
        promptBody: '对上游返回的缓存命中输入 token 使用更低计费倍率。',
        readRatio: '读取倍率',
        creationRatio: '写入倍率',
        diskFiles: '磁盘文件数',
        diskUsage: '磁盘占用',
        memoryBuffers: '内存缓存数',
        memoryUsage: '内存占用',
        diskHits: '磁盘命中',
        memoryHits: '内存命中',
        promptCacheHit: 'Prompt Cache 命中',
        estimatedSaved: '预估节省',
        tokens: 'tokens',
        reload: '刷新',
        resetStats: '重置缓存统计',
        cleanDisk: '清理过期磁盘缓存',
        save: '保存缓存设置',
        saved: '缓存设置已保存。',
        resetDone: '缓存统计已重置。',
        cleaned: (count) => `已清理 ${count} 个磁盘缓存文件。`
      },
      business: {
        loadFailed: '管理员看板数据加载失败',
        period: '周期',
        sales: '销售额',
        estimatedCost: '预估成本',
        estimatedProfit: '预估毛利',
        today: '今日',
        sevenDays: '7 日',
        thirtyDays: '30 日',
        title: '管理员看板',
        subtitle: '用户、收入、渠道、模型调用和运营配置集中管理。',
        channelManagement: '渠道管理',
        refresh: '刷新数据',
        totalUsers: '总用户数',
        totalUsersBody: '包含普通用户和管理员账户',
        thirtyDaySales: '30 日销售额',
        salesBody: '按网关额度折算美元',
        availableChannels: '可用渠道',
        channelSummary: (disabled, lowBalance) => `${disabled} 个不可用，${lowBalance} 个低余额`,
        estimatedCostRate: '预估成本率',
        costRateBody: '可在运营设置中调整',
        salesAndCost: '销售额与成本',
        channelStatus: '渠道状态',
        manageChannels: '管理渠道',
        channel: '渠道',
        provider: '供应商',
        status: '状态',
        remainingQuota: '剩余额度',
        usedQuota: '已用额度',
        response: '响应',
        modelRanking: '模型调用排行 / 30 日',
        emptyModelUsage: '暂无调用数据',
        model: '模型',
        requests: '请求数',
        available: '可用',
        disabled: '禁用',
        autoBanned: '自动禁用',
        unknown: '未知'
      },
    },
  },
};

const LanguageContext = createContext(null);

export const getLanguage = () => {
  const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return SUPPORTED_LANGUAGES.includes(stored) ? stored : 'en';
};

export const setLanguage = (language) => {
  const nextLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : 'en';
  localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
  document.documentElement.lang = nextLanguage === 'zh' ? 'zh-CN' : 'en';
  window.dispatchEvent(new CustomEvent('gptokens-language-change', { detail: nextLanguage }));
};

export const getLanguagePack = (language) => dictionaries[language] || dictionaries.en;

export const translate = (language, path, fallback = '') => {
  const dictionary = dictionaries[language] || dictionaries.en;
  const english = dictionaries.en;
  const value = path.split('.').reduce((current, key) => current?.[key], dictionary);
  if (value !== undefined) return value;
  const fallbackValue = path.split('.').reduce((current, key) => current?.[key], english);
  return fallbackValue !== undefined ? fallbackValue : fallback;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getLanguage);

  useEffect(() => {
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';
    const syncLanguage = (event) => {
      setLanguageState(event?.detail || getLanguage());
    };
    window.addEventListener('gptokens-language-change', syncLanguage);
    window.addEventListener('storage', syncLanguage);
    return () => {
      window.removeEventListener('gptokens-language-change', syncLanguage);
      window.removeEventListener('storage', syncLanguage);
    };
  }, [language]);

  const value = useMemo(() => {
    const pack = getLanguagePack(language);
    const changeLanguage = (nextLanguage) => {
      setLanguage(nextLanguage);
      setLanguageState(SUPPORTED_LANGUAGES.includes(nextLanguage) ? nextLanguage : 'en');
    };
    const t = (path, fallback) => translate(language, path, fallback);
    const ns = (path, fallback = {}) => {
      const value = path.split('.').reduce((current, key) => current?.[key], pack);
      if (value !== undefined) return value;
      const fallbackValue = path.split('.').reduce((current, key) => current?.[key], dictionaries.en);
      return fallbackValue !== undefined ? fallbackValue : fallback;
    };
    return {
      language,
      setLanguage: changeLanguage,
      pack,
      t,
      ns,
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  const [language, setLanguageState] = useState(getLanguage);

  useEffect(() => {
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';
    const syncLanguage = (event) => {
      setLanguageState(event?.detail || getLanguage());
    };
    window.addEventListener('gptokens-language-change', syncLanguage);
    window.addEventListener('storage', syncLanguage);
    return () => {
      window.removeEventListener('gptokens-language-change', syncLanguage);
      window.removeEventListener('storage', syncLanguage);
    };
  }, [language]);

  const changeLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    setLanguageState(nextLanguage);
  };

  const legacyValue = {
    language,
    setLanguage: changeLanguage,
    t: (path, fallback) => translate(language, path, fallback),
    pack: getLanguagePack(language),
    ns: (path, fallback = {}) => {
      const pack = getLanguagePack(language);
      const value = path.split('.').reduce((current, key) => current?.[key], pack);
      if (value !== undefined) return value;
      const fallbackValue = path.split('.').reduce((current, key) => current?.[key], dictionaries.en);
      return fallbackValue !== undefined ? fallbackValue : fallback;
    },
  };
  return context || legacyValue;
};

export const useLanguagePack = (namespace) => {
  const { ns } = useLanguage();
  return ns(namespace);
};
